'use strict';
namespace Core {

    import BookingType = Core.ManageBooking.BookingType;
    import CrossSellType = Core.Booking.CrossSellType;
    import CustomerDashboardService = Core.Customer.CustomerDashboardService;
    import FlightBooking = Core.ManageBooking.FlightBooking;

    type CrossSellOption = Core.Customer.CrossSellOption;

    interface RouteParams extends ng.route.IRouteParamsService
    {
        ref :string;
        source :string;
        tripId :string;
        version :number;
    }

    interface ProductBreakdown
    {
        cardFee :{ total :number; currencyCode :string; };
        items :ProductBreakdownItem[];
        packageTotal :number;
    }

    interface ProductBreakdownItem
    {
        name :string;
        breakdown :{ title :string; quantity :number; total :number; supplierRef :string; typeId :number; }[];
        isPackageProduct :boolean;
    }

    interface TrackingUrls {
        responsysUrl :string;
        trivagoUrl :string;
        kayakUrl :string;
        skyscannerUrl :string;
    }


    class BookingConfirmationCtrl {

        public adults :number;
        public authModalOptions :Object;
        public carTrawlerUrl :string;
        public children :number;
        public crossSells :CrossSellOption[] = [];
        public defaultColSizePromo :string = 'col-m-9 col-l-9';
        public defaultColSizeCrossSell :string = 'col-m-3 col-l-3';
        public destinationImageUrl :string;
        public destinationTitle :string;
        public infants :number;
        public isAgentMode :boolean;
        public isSignedIn :boolean;
        public loadingCrossSells :boolean = true;
        public manageBookingUrl :string;
        public showBreakdown :boolean = false;
        public showCarTrawlerWidget :boolean = false;
        public showEmailInformation :boolean = false;
        public trackingUrls :TrackingUrls;
        public usersFirstName :string;

        public attraction :Core.ManageBooking.AttractionsBooking;
        public carhire :Core.ManageBooking.CarHireBooking;
        public flight :Core.ManageBooking.FlightBooking;
        public hotel :Core.ManageBooking.Hotel;
        public insurance :Core.ManageBooking.TravelInsuranceBooking;
        public parking :Core.ManageBooking.AirportParkingBooking;
        public transfer :Core.ManageBooking.TransferBooking;
        public paymentDetails :Core.ManageBooking.PaymentDetails;
        public breakdown :ProductBreakdown;

        private readonly bookingTypeProductLabelMap : {[id: number]: () => string} = {};

        constructor(
            private $location :ng.ILocationService,
            private $routeParams :RouteParams,
            private $scope :ng.IScope,
            private $window :ng.IWindowService,
            private analytics : any,
            private authService :any,
            private bookingsManagerService :ManageBooking.BookingsManagerService,
            private customerDashboardService :Core.Customer.CustomerDashboardService,
            private dateUtil :any,
            private resourceService :ResourceService,
            private costBreakdownCodes :Core.CostBreakdownCodes
        ) {
            this.$scope.$emit('block', {
                title: 'Booking Completed',
                loadingScreenStyle: 'booking'
            });

            if (TRPCONFIG.Domain.HideBookingRefOnConfirmation) {
                this.$location.replace();
                this.$location.url('booking/complete');
            }

            this.trackEvent('ConfPageLanding', null);

            this.analytics.track('Facebook', 'BookingComplete');

            this.isAgentMode = TRPCONFIG.Domain.isAgentMode;

            //set up our labels that might be needed
            const addProperty = (obj :{[id: number]: () => string}, id :BookingType, resourceKey :string) => {
                obj[id] = this.resourceService.getDeferred(resourceKey);
            };

            addProperty(this.bookingTypeProductLabelMap, BookingType.Accommodation, 'CONFIRMATION_HOTEL');
            addProperty(this.bookingTypeProductLabelMap, BookingType.AdHoc, 'CONFIRMATION_ADHOC');
            addProperty(this.bookingTypeProductLabelMap, BookingType.AttractionTickets, 'CONFIRMATION_ATTRACTIONS');
            addProperty(this.bookingTypeProductLabelMap, BookingType.CarHire, 'CONFIRMATION_CARHIRE');
            addProperty(this.bookingTypeProductLabelMap, BookingType.Flight, 'CONFIRMATION_FLIGHT');
            addProperty(this.bookingTypeProductLabelMap, BookingType.Parking, 'CONFIRMATION_PARKING');
            addProperty(this.bookingTypeProductLabelMap, BookingType.Transfer, 'CONFIRMATION_TRANSFER');
            addProperty(this.bookingTypeProductLabelMap, BookingType.TravelInsurance, 'CONFIRMATION_INSURANCE');


            //a bit of a nasty hack to deal with encoding and users signing in on the booking complete page. The
            //ref param gets encoded twice if they sign in on the complete page so we need to handle this.
            //This fix is not ideal, but until I can check that we can change the URL structure st that the booking refs
            //are QS parameters this will do the trick
            const bookingRefs = decodeURIComponent(this.$routeParams.ref).split('|'); // ACM/123456|EUF/123456|...

            let bookingId = Number(bookingRefs[0].split('/')[1]);

            bookingsManagerService.getTrip(bookingId).then(trip => {
                if (trip.Destination) {
                    this.destinationImageUrl = trip.Destination.ImageUrl;
                    this.destinationTitle = trip.Destination.Title;
                }

                this.adults = trip.Adults;
                this.children = trip.Children;
                this.infants = trip.Infants;

                this.attraction = trip.AttractionsBooking;
                this.carhire = trip.CarHireBooking;
                this.hotel = trip.HotelBooking;
                this.flight = trip.FlightBooking;
                this.insurance = trip.InsuranceBooking;
                this.parking = trip.AirportParkingBooking;
                this.transfer = trip.TransferBooking;
                this.paymentDetails = trip.PaymentDetails;

                //for the purposes of cross sell always make sure the booking Id is that of the flight (if available)
                bookingId = this.flight && this.flight.BookingId || bookingId;

                this.customerDashboardService.getAllCrossSellOptions(bookingId)
                    .then(crossSells => {
                        //we want to remove any car hire crosssells as we are doing this via the widget
                        _.remove(crossSells, (c :CrossSellOption) => c.CrossSellType === CrossSellType.CarHire);
                        const orderedCrossSells = this.orderCrosssells(trip, crossSells);
                        this.crossSells = _.take(orderedCrossSells, 1);
                    }).finally( () => {
                        //based on the length of the cross sell options we might want the app promo / carTrawler widget
                        //to take the full width
                        let colSize = 12;
                        let colSizeCrossSell = 0;

                        if (this.crossSells.length > 0) {
                            colSize = 12 - (this.crossSells.length * 3);
                            colSizeCrossSell = (12 - colSize) / this.crossSells.length;
                        }
                        this.defaultColSizeCrossSell = `col-m-${colSizeCrossSell} col-l-${colSizeCrossSell}`;
                        this.defaultColSizePromo = `col-m-${colSize} col-l-${colSize}`;
                        this.loadingCrossSells = false;
                    });

                //restructure our price breakdown lines so there is less typing for us to do in the template as these
                //are all grouped together
                this.breakdown = this.groupProductBreakdowns(trip);

                //flag which item we want to show, the app promo or the cartrawler widget
                this.showCarTrawlerWidget = trip.FlightBooking && !trip.TransferBooking && !trip.CarHireBooking;
                if (this.showCarTrawlerWidget) {
                    this.carTrawlerUrl = this.buildCarTrawlerUrl(trip.FlightBooking,
                                                                 trip.Adults + trip.Children + trip.Infants);
                }

                this.trackEvent('ConfPageCarWidget', { ShowWidget: this.showCarTrawlerWidget, Url: this.carTrawlerUrl});

            }).finally( () => {
                this.$scope.$emit('unblock');
            });

            const user = authService.getUser();
            this.isSignedIn = user.HardSignedIn;
            this.usersFirstName = user.FirstName;
            this.authModalOptions = { email: user.Email };
            this.manageBookingUrl = `dashboard/bookings/reference/${bookingId}`;
        }

        public showModal(show :boolean, event :JQueryEventObject) :void {
            if (event) {
                event.preventDefault();
            }
            if (show === true) {
                this.trackEvent('ConfPagePriceClick', null);
            }
            this.showBreakdown = show;
        }

        public showEmailInformationModal(show :boolean, event :JQueryEventObject) :void {
            if (event) {
                event.preventDefault();
            }
            if (show === true) {
                this.trackEvent('ConfPageInfoClick', null);
            }
            this.showEmailInformation = show;
        }

        public printPage() :void {
            this.trackEvent('ConfPagePrintClick', null);
            this.$window.print();
        }

        public trackEvent(eventName, data) :void {
            this.analytics.track('mixpanel', eventName, data);
        }

        public trackCrossSellClick(model) :void {
            let data =  {
                Type : CrossSellType[model.CrossSellType]
            };
            this.analytics.track('mixpanel', 'ConfPageCrossSellClick', data);
        }

        public getProductLabelForBookingType(bookingType :BookingType) :() => string {
            //we only need to handle a couple of cases for now, but have left
            //this general enough to apply to others
            return this.bookingTypeProductLabelMap[bookingType];
        }

        private buildCarTrawlerUrl(flight :FlightBooking, passengerCount :number) :string {

            //build up the cartrawler url NOVERSION param is so that we don't add cache busting to it
            const outLeg = _.last(_.filter(flight.Legs, {'IsOutbound':true}));
            const inLeg = _.first(_.filter(flight.Legs, {'IsOutbound':false}));

            const amendDate = (date :string, hourOffset :number) => {
                return this.dateUtil.format(
                    this.dateUtil.roundedToStartOfHour(this.dateUtil.addHours(date, hourOffset), true),
                    'yyyyMMddHHmm'
                );
            };

            const pickUp = amendDate(outLeg.ArrivalTime, TRPCONFIG.CarHireFlightDepartureOffset);
            const dropOff = amendDate(inLeg.DepartureTime, TRPCONFIG.CarHireFlightReturnOffset);

            //not pretty, but multiline string adds carriage returns in the output (which we do not want)
            const base = `https://cars.cartrawler.com/travelrepublic/widget?type=html&clientId=740760&NOVERSION=-1&` +
                         `num=3&locCode={0}&curr={1}&residencyId={2}&pkDateTime={3}&rtDateTime={4}&passengers={5}&` +
                         `airline={6}&airlineNum={7}`;

            return String.format(
                            base,
                            outLeg.ArrivalAirport.Code,
                            TRPCONFIG.Domain.CurrencyCode,
                            TRPCONFIG.Domain.CountryCode,
                            pickUp,
                            dropOff,
                            passengerCount,
                            outLeg.Carrier,
                            outLeg.FlightNo.slice(-4) //last 4 should be the flight number
                    );
        }

        private orderCrosssells(trip :ManageBooking.BookedTrip, crossSells :CrossSellOption[]) :CrossSellOption[] {
            let orderedList = new Array();

            const orderPreference = {
                Holiday: [  CrossSellType.Transfers,
                            CrossSellType.CarHire,
                            CrossSellType.Baggage,
                            CrossSellType.Seats,
                            CrossSellType.Attractions
                         ],
                Hotel: [
                            CrossSellType.Transfers,
                            CrossSellType.CarHire,
                            CrossSellType.Attractions,
                            CrossSellType.Parking
                        ],
                Flight: [
                            CrossSellType.Hotels,
                            CrossSellType.Baggage,
                            CrossSellType.Seats,
                            CrossSellType.CarHire,
                            CrossSellType.Parking,
                            CrossSellType.Attractions ]
            };

            let preference :CrossSellType[]|null = null;
            if (trip.HotelBooking) {
                preference = trip.FlightBooking ? orderPreference.Holiday : orderPreference.Hotel;
            } else if(trip.FlightBooking) {
                preference = orderPreference.Flight;
            } else {
                return orderedList;
            }

            //make an object from our preference array to make sorting a little easier
            const sortMap = preference.reduce(function(acc, val, idx) {
                acc[val] = idx;
                return acc;
            }, {});

            orderedList = _.sortBy(crossSells, c => sortMap[c.CrossSellType]);

            return orderedList;
        }

        private groupProductBreakdowns(trip :ManageBooking.BookedTrip) :ProductBreakdown {
            let breakdown = {
                cardFee: {
                    total: 0, currencyCode: ''
                },
                items: <ProductBreakdownItem[]>[],
                packageTotal: 0,
                discount: 0
            };

            const createBreakdownItem =
                (title: string, quantity :number, total :number, currencyCode :string, isPriceHidden: boolean,
                    supplierRef: string, typeId: number) => {
                    return { currencyCode, quantity, title, total, isPriceHidden, supplierRef, typeId };
                };

            _.each(
                [trip.FlightBooking, trip.HotelBooking, trip.AirportParkingBooking, trip.AttractionsBooking,
                    trip.CarHireBooking, trip.InsuranceBooking, trip.TransferBooking],
                (p :ManageBooking.ProductBooking) => {
                    if (p) {
                        _.each(
                                _.filter(p.PriceBreakdown, {'IsPaymentFee' : true}),
                                b => {
                                    breakdown.cardFee.total += b.UnitAmount;
                                    breakdown.cardFee.currencyCode = b.CurrencyCode;
                                }
                        );

                        const item = {
                            name: this.getProductLabelForBookingType(p.BookingType)(),
                            breakdown: _.map(
                                            _.filter(p.PriceBreakdown, {'IsPaymentFee' : false}),
                                            b => createBreakdownItem(b.Description,
                                                                     b.Quantity,
                                                                     b.UnitAmount * b.Quantity,
                                                                     b.CurrencyCode,
                                                                     b.IsPriceHidden,
                                                                     b.SupplierRef || '',
                                                                     b.TypeId)
                            ),
                            isPackageProduct: !!p.PackageId,
                            discount: 0
                        };

                        // Sum discounts
                        item.breakdown.forEach((value) =>{
                            if(value.typeId === 20){ // Where id 20 is discounts
                                item.discount += (value.total * value.quantity);
                            }
                        });

                        // Sum invisible price with hotel
                        item.breakdown = item.breakdown.map(breakdown => {
                            if(breakdown.typeId === 1){ // Where id 1 is id of the hotel
                                item.breakdown.forEach( invisible => {
                                    // Where id 0 is id of the invisible price
                                    if(invisible.typeId === 0 && invisible.isPriceHidden){
                                        breakdown.total += invisible.total;
                                    }
                                });
                            }

                            return breakdown;
                        });

                        if(item.isPackageProduct) {
                            breakdown.packageTotal =
                                _.reduce(item.breakdown, (r, b) =>
                                    r + (b.typeId === 0 && b.isPriceHidden ? 0 : b.total),
                                    breakdown.packageTotal);
                        }
                        breakdown.items.push(item);

                        breakdown.discount += item.discount;
                    }
                }
            );

            return breakdown;
        }
    }

    angular
        .module('core')
        .controller('BookingConfirmation', BookingConfirmationCtrl);
}
